import { DT } from "../../constants.ts";
import { add, scale, viewDir } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, MoveKind, setHv } from "../../sim/components.ts";
import { addOverhealth, applyDamage, applyImpulse, applyStatus, coneConstructs, coneTargets, damageBarrier, isEnemy, playersInRadius } from "../../sim/combat.ts";
import { damageConstruct } from "../../sim/constructs.ts";
import { beginTargeting, endTargeting, targetConfirmed, targetingSlot, targetPoint } from "../../sim/targeting.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, pk, type HeroModule } from "../module.ts";
import { action, charge, commonInput, consume, contactEnemies, launchProjectile, ownerAlive, ready, secs, simpleProjectile, trigger } from "../kitlib.ts";

export const V = { barrierUp: 0, barrierHp: 1, barrierRegenDelay: 2, rally: 3, combo: 4, dash: 5, slam: 6 } as const;
export const PK = { Shield: pk(HeroId.CaptainAmericano, 0) } as const;
export const FX = { Shield: fxk(HeroId.CaptainAmericano, 0), Rally: fxk(HeroId.CaptainAmericano, 1), Slam: fxk(HeroId.CaptainAmericano, 2) } as const;
export const AMERICANO_BARRIER = { upVar: V.barrierUp, hpVar: V.barrierHp, maxHp: 350, width: 2.3, height: 2.3, forward: 1.15, regen: 50, regenDelay: 240 } as const;

const def: HeroDef = {
  id: HeroId.CaptainAmericano,
  slug: "captain-americano",
  name: "Captain Americano",
  role: Role.Tank,
  color: "#2f6fe0",
  blurb: "Shield-first brawler who blocks fire, ricochets throws, leaps in, and rallies a team-wide push.",
  health: 350,
  armor: 0,
  shields: 300,
  radius: 0.5,
  height: 1.95,
  headRadius: 0.25,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 3100,
  abilities: {
    [Slot.Primary]: { name: "Sentinel Slugger", key: "LMB", description: "Punch with the shield for 45 damage. Every second hit primes a shield ricochet.", cooldown: 0 },
    [Slot.Secondary]: { name: "Mesa Guard", key: "RMB (hold)", description: "Raise a 350-HP shield to block enemy fire in front of you.", cooldown: 1 },
    [Slot.Ability1]: { name: "Leading Sprint", key: "Shift", description: "Gain 33.4% speed, then jump and LMB to slam a targeted area.", cooldown: 12 },
    [Slot.Ability2]: { name: "Café-Saw Toss", key: "E", description: "Throw the shield 25m for a heavy 70 damage hit.", cooldown: 6 },
    [Slot.Ability3]: { name: "Banner Bash", key: "F", description: "Charge 12m with the shield, gaining unstoppable and bonus health.", cooldown: 10 },
    [Slot.Ultimate]: { name: "Freedom Stampede", key: "Q", description: "Lead a 10 second, 4m push that grants nearby allies bonus health and speed.", cooldown: 0 },
  },
  passive: { name: "Second Serve", description: "Landing shield punches builds a short-lived shield throw follow-up." },
};

function shieldPunch(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, inputYaw: number) {
  const c = sim.c;
  let hit = false;
  for (const t of coneTargets(sim, eid, 4, 0.72, (e) => isEnemy(sim, eid, e))) {
    hit = true;
    applyDamage(sim, t, 45, eid, { pos: add(sim.posOf(t), { x: 0, y: 1, z: 0 }) });
    const d = viewDir(inputYaw, 0);
    applyImpulse(sim, t, { x: d.x * 3.5, y: 1.1, z: d.z * 3.5 });
  }
  for (const { eid: t } of coneConstructs(sim, eid, 4, 0.72)) {
    hit = true;
    damageConstruct(sim, t, 45, eid);
  }
  if (hit) setHv(c, eid, V.combo, Math.min(2, hv(c, eid, V.combo) + 1));
  return hit;
}

const module: HeroModule = {
  def,
  barrier: AMERICANO_BARRIER,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.barrierHp, AMERICANO_BARRIER.maxHp);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      const delay = hv(c, eid, V.barrierRegenDelay);
      if (delay > 0) setHv(c, eid, V.barrierRegenDelay, delay - 1);
      else if (!hv(c, eid, V.barrierUp)) setHv(c, eid, V.barrierHp, Math.min(AMERICANO_BARRIER.maxHp, hv(c, eid, V.barrierHp) + AMERICANO_BARRIER.regen * DT));
      if (hv(c, eid, V.rally) > 0) {
        setHv(c, eid, V.rally, hv(c, eid, V.rally) - 1);
        for (const { eid: a } of playersInRadius(sim, sim.posOf(eid), 4, (e) => !isEnemy(sim, eid, e), false)) {
          addOverhealth(sim, a, a === eid ? 150 : 100, 25, eid);
          applyStatus(sim, a, "haste", 0.25, { hastePct: 0.3, source: eid });
        }
      }
      if (hv(c, eid, V.dash) > 0) setHv(c, eid, V.dash, hv(c, eid, V.dash) - 1);
      if (hv(c, eid, V.slam) > 0) {
        if (targetConfirmed(sim, eid, Slot.Ability1)) {
          const p = targetPoint(sim, eid, Slot.Ability1).pos;
          c.Pos.x[eid] = p.x;
          c.Pos.y[eid] = p.y;
          c.Pos.z[eid] = p.z;
          c.Vel.y[eid] = -4;
          c.Player.grounded[eid] = 1;
          sim.emit({ type: "explosion", pos: add(p, { x: 0, y: 0.2, z: 0 }), radius: 4, kind: FX.Slam });
          for (const { eid: t } of playersInRadius(sim, p, 4, (e) => isEnemy(sim, eid, e), false)) {
            applyDamage(sim, t, 30, eid, { pos: p });
            c.Vel.y[t] = Math.max(c.Vel.y[t]!, 7);
            c.Player.grounded[t] = 0;
          }
          setHv(c, eid, V.slam, 0);
          setHv(c, eid, V.dash, 0);
          endTargeting(sim, eid);
        } else if (!canAct || (targetingSlot(sim, eid) === Slot.Ability1 && c.Player.grounded[eid] && c.Vel.y[eid]! <= 0)) {
          setHv(c, eid, V.slam, 0);
          endTargeting(sim, eid);
        }
      }

      commonInput(sim, eid, input, pressed);
      const barrierHeld = canAct && !!(input.buttons & Btn.Secondary) && hv(c, eid, V.barrierHp) > 0;
      setHv(c, eid, V.barrierUp, barrierHeld ? 1 : 0);
      if (!canAct) return;
      if (hv(c, eid, V.slam) > 0 && targetingSlot(sim, eid) === Slot.Ability1) return;

      if ((pressed & Btn.Ultimate) && c.Ult.charge[eid]! >= 1) {
        c.Ult.charge[eid] = 0;
        setHv(c, eid, V.rally, secs(10));
        action(sim, eid, ActionKind.Ult, 45);
        sim.emit({ type: "explosion", pos: add(sim.posOf(eid), { x: 0, y: 1, z: 0 }), radius: 4, kind: FX.Rally });
      }
      if ((pressed & Btn.Ability3) && ready(sim, eid, Slot.Ability3)) {
        consume(sim, eid, Slot.Ability3);
        applyStatus(sim, eid, "unstoppable", 0.63, { source: eid });
        addOverhealth(sim, eid, 50, secs(4), eid);
        charge(sim, eid, input, { speed: 19, seconds: 0.63 });
        action(sim, eid, ActionKind.Cast, 18);
      }
      if (c.Move.kind[eid] === MoveKind.Charge) {
        contactEnemies(sim, eid, 1.15, (t) => {
          applyDamage(sim, t, 30, eid);
          applyImpulse(sim, t, scale(viewDir(input.yaw, 0), 7));
        });
      }
      if ((pressed & Btn.Ability1) && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        setHv(c, eid, V.dash, secs(5));
        applyStatus(sim, eid, "haste", 5, { hastePct: 0.334, source: eid });
        action(sim, eid, ActionKind.Cast, 12);
      }
      if ((pressed & Btn.Jump) && hv(c, eid, V.dash) > 0 && c.Player.grounded[eid]) {
        c.Vel.y[eid] = 9.6;
        const d = viewDir(input.yaw, 0);
        c.Ext.x[eid] += d.x * 8;
        c.Ext.z[eid] += d.z * 8;
        c.Player.grounded[eid] = 0;
        setHv(c, eid, V.slam, 1);
        beginTargeting(sim, eid, Slot.Ability1);
      }
      if ((pressed & Btn.Ability2) && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 18);
        launchProjectile(sim, eid, PK.Shield, 80, input, { radius: 0.5, seconds: 0.3125 });
      }
      if (!barrierHeld && trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) {
        c.Weapon.fireCd[eid] = 27;
        action(sim, eid, ActionKind.Fire, 12);
        shieldPunch(sim, eid, input.yaw);
        if (hv(c, eid, V.combo) >= 2) {
          setHv(c, eid, V.combo, 0);
          launchProjectile(sim, eid, PK.Shield, 60, input, { radius: 0.45, seconds: 0.4167 });
        }
      }
    },
    movement(sim, eid, _input, mods) {
      if (hv(sim.c, eid, V.barrierUp)) mods.speedMul *= 0.55;
      if (hv(sim.c, eid, V.rally) > 0) mods.speedMul *= 1.3;
    },
  },
  projectiles: {
    [PK.Shield]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        if (hit.target !== undefined) {
          applyDamage(sim, hit.target, 70, owner, { pos: hit.pos });
          applyImpulse(sim, hit.target, { x: sim.c.Vel.x[_p]! * 0.08, y: 1.5, z: sim.c.Vel.z[_p]! * 0.08 });
        } else if (hit.construct !== undefined) damageConstruct(sim, hit.construct, 70, owner);
        else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 70, owner);
        sim.emit({ type: "impact", pos: hit.pos, kind: FX.Shield });
      },
      { color: "#f4f6ff", size: 0.32, trail: true },
    ),
  },
  fx: {
    [FX.Shield]: { color: "#f4f6ff" },
    [FX.Rally]: { color: "#2f6fe0" },
    [FX.Slam]: { color: "#8fb3ff" },
  },
  targets: {
    [Slot.Ability1]: { start: "kit", range: 20, radius: 4, verb: "Slam" },
  },
};

export default module;
